import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { OutputFormat, TranscriptionResult } from '../../types/transcription'
import { RTL_LANGUAGE_CODES, TRANSLATE_LANGUAGES } from '../../lib/languages'
import { EXPORT_FORMATS, cuesFromFormats, formatLabel, serializeCues, type Cue } from '../../lib/subtitleConvert'
import { subtitleDocDir } from '../../lib/textDirection'
import { polishFormats, transcriptNeedsCapitalization } from '../../lib/textPolish'
import { segmentCues, segmentationChanges, type LoudnessEnvelope } from '../../lib/subtitleSegment'
import { sortLanguageOptions } from '../../lib/languageOptions'
import { loudnessEnvelope } from '../../lib/audioEnvelope'
import type { PlaybackAudio, PlaybackSpan } from '../../lib/playbackAudio'
import { isWma } from '../../lib/asf'
import { formatDuration, roundUpToMinute } from '../../lib/formatDuration'
import type { TranscriptionProgress } from '../../hooks/useTranscription'
import { readSegmentPref, segmentDefaultFor, writeSegmentPref } from '../../lib/segmentPref'
import { saveHistoryEntry, updateHistoryFormats, updateHistorySegmented } from '../../lib/historyStore'
import { apiGet, apiPost } from '../../api/client'
import { trackTranslated, trackCueEdited, trackCueDeleted, trackSubtitleSegmentation } from '../../analytics/posthog'
import { useI18n, useT } from '../../i18n/useT'
import { LoadingSpinner } from '../ui/LoadingSpinner'
import { Toast } from '../ui/Toast'
import { Select } from '../ui/Select'
import { PILL_SWITCH_BUTTON, PILL_SWITCH_FRAME } from '../ui/pillSwitch'
import { CopyButton } from './CopyButton'
import { DownloadButton } from './DownloadButton'
import { DownloadAllButton } from './DownloadAllButton'
import { CuePlayer } from './CuePlayer'

interface ResultPanelProps {
  isLoading: boolean
  result: TranscriptionResult | null
  error: string | null
  onReset: () => void
  language?: string
  /** Display format chosen in the form — switching it is free, no new request */
  format: OutputFormat
  /** Uploaded file metadata for the local (browser-only) history entry */
  sourceName?: string
  sourceSize?: number
  /** SHA-256 of the uploaded file — stored in history for duplicate detection */
  sourceHash?: string
  /** The uploaded file itself — played locally in the interactive view */
  sourceFile?: File | null
  /** For a recording that was cut into parts: the parts glued back together,
   * which — unlike a VBR original — the browser can seek. `playbackAudio.ts`
   * has the measurements. */
  playbackAudio?: PlaybackAudio | null
  /** 0-100 while the upload is in flight, null otherwise */
  uploadProgress?: number | null
  /** What the run is doing, in its own words rather than inferred from the
   * percentage. Absent for a result that is not being produced right now. */
  progress?: TranscriptionProgress
  /** Stops a run in flight. Offered beside the bar, where the person waiting
   * is already looking. */
  onCancel?: () => void
}

interface TranslateApiResponse {
  segments: string[]
  target_lang: string
}

interface FormatsApiResponse {
  formats: Record<string, string>
  complete: boolean
}

const FORMATS_POLL_INTERVAL_MS = 2_000
const FORMATS_POLL_TIMEOUT_MS = 4 * 60_000

/** Stable identity: an inline [] would be a new array on every render, and
 * the player's effects key on it. */
const EMPTY_SPANS: PlaybackSpan[] = []

export function ResultPanel({ isLoading, result, error, onReset, language, format, sourceName, sourceSize, sourceHash, sourceFile, playbackAudio, uploadProgress, progress, onCancel }: ResultPanelProps) {
  const t = useT()
  const { locale } = useI18n()
  const targetOptions = useMemo(
    () =>
      sortLanguageOptions(
        [
          { value: '', label: t('result.original') },
          // The translator reaches further than the recogniser: 110 targets against
          // the 93 languages a file can be transcribed in
          ...TRANSLATE_LANGUAGES.map((lang) => ({ value: lang.code, label: t(`langName.${lang.code}`) })),
        ],
        locale,
      ),
    [t, locale],
  )
  const [showCopied, setShowCopied] = useState(false)
  const [targetLang, setTargetLang] = useState('')
  // Interactive (player + cues) vs raw format text. Text is always the
  // default — the player needs timestamps that arrive a few seconds later,
  // and yanking the view away from a reading user is worse than one click.
  const [view, setView] = useState<'player' | 'text'>('text')
  // Per-language cache of translated cue texts (aligned with masterCues)
  const [translations, setTranslations] = useState<Record<string, string[]>>({})
  const [isTranslating, setIsTranslating] = useState(false)
  const [translateError, setTranslateError] = useState<string | null>(null)
  // Server-generated output formats, fetched in the background after the
  // charged request: {format: content}
  const [serverFormats, setServerFormats] = useState<Record<string, string>>({})
  const [formatsComplete, setFormatsComplete] = useState(false)
  // User-corrected cues. Once set, every view/download derives client-side
  // from these instead of the server texts, and history holds the edits.
  const [editedCues, setEditedCues] = useState<Cue[] | null>(null)
  const hasEditsRef = useRef(false)
  // What the cues were before the last delete, so it can be taken back
  const undoRef = useRef<{
    cues: Cue[] | null
    hadEdits: boolean
    segmented: boolean
    translations: Record<string, string[]>
  } | null>(null)
  const [showUndo, setShowUndo] = useState(false)
  // Subtitle segmentation, plus the loudness envelope that lets the re-cut
  // land on actual pauses. Never touched the switch? Someone who asked for
  // SRT came for subtitles — open on those rather than on a transcript they
  // would have to re-cut by hand.
  // `format` (the picker's value), not result.output_format: the panel mounts
  // while the transcription is still running, when there is no result yet
  const [segmentOn, setSegmentOn] = useState(() => readSegmentPref(segmentDefaultFor(format)))
  const [envelope, setEnvelope] = useState<LoudnessEnvelope | null>(null)
  const [envelopeReady, setEnvelopeReady] = useState(false)

  const handleCopied = useCallback(() => setShowCopied(true), [])
  const handleHideCopied = useCallback(() => setShowCopied(false), [])
  const handleHideUndo = useCallback(() => setShowUndo(false), [])

  // Some engine variants return caseless text ("today i want…"). When the
  // charged result looks like that, every format entering the panel is run
  // through the polish — so views, downloads, translations and the history
  // copy all inherit it. Properly cased results are never touched.
  const needsPolish = useMemo(
    () => !!result?.result && transcriptNeedsCapitalization(result.result, result.output_format),
    [result],
  )

  // New transcription → reset translation state and poll the free formats
  // endpoint until the background fetch finishes
  useEffect(() => {
    setTargetLang('')
    setTranslations({})
    setTranslateError(null)
    const seed = result?.result
      ? needsPolish
        ? polishFormats({ [result.output_format]: result.result }, language)
        : { [result.output_format]: result.result }
      : {}
    setServerFormats(seed)
    setFormatsComplete(false)
    setEditedCues(null)
    hasEditsRef.current = false
    undoRef.current = null
    setShowUndo(false)
    if (!result?.job_id || !result.result) return

    // Every new result opens on the chosen format's text; the player is
    // opt-in once its tab becomes ready
    setView('text')
    // The lasting copy lives in the browser: the server erases texts in hours
    void saveHistoryEntry({
      id: result.job_id,
      filename: sourceName || 'audio',
      language: language || '',
      format: result.output_format,
      fileSize: sourceSize ?? 0,
      createdAt: Date.now(),
      formats: seed,
      complete: false,
      hash: sourceHash,
    })

    let cancelled = false
    const startedAt = Date.now()

    async function poll() {
      while (!cancelled && Date.now() - startedAt < FORMATS_POLL_TIMEOUT_MS) {
        await new Promise((r) => setTimeout(r, FORMATS_POLL_INTERVAL_MS))
        if (cancelled) return
        try {
          const resp = await apiGet<FormatsApiResponse>(`/transcriptions/${result!.job_id}/formats`)
          if (cancelled) return
          const incoming = needsPolish ? polishFormats(resp.formats, language) : resp.formats
          setServerFormats(incoming)
          // Once the user edited the text, history keeps the edited formats —
          // late server formats must not overwrite them
          if (!hasEditsRef.current) {
            void updateHistoryFormats(result!.job_id, incoming, resp.complete)
          }
          if (resp.complete) {
            setFormatsComplete(true)
            return
          }
        } catch {
          // transient — keep polling until the timeout
        }
      }
      // The server sweep never reported `done` within the window — stop
      // waiting and let client-side conversion cover whatever is missing
      if (!cancelled) setFormatsComplete(true)
    }

    void poll()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result?.job_id])

  // Timed cues from the best available server format — used for translation
  // and as a fallback source for formats the server didn't deliver. User
  // edits take precedence once they exist.
  const baseCues = useMemo<Cue[] | null>(() => cuesFromFormats(serverFormats), [serverFormats])
  // The same words re-cut into subtitle-sized lines. Derived from the cues we
  // already have, so the switch costs no request and no credit.
  const segmentedCues = useMemo<Cue[] | null>(
    () => (baseCues ? segmentCues(baseCues, envelope) : null),
    [baseCues, envelope],
  )
  // A transcript whose blocks already fit the subtitle norm comes back
  // untouched — then the switch would do nothing and isn't shown at all
  const canSegment = !!(baseCues && segmentedCues && segmentationChanges(baseCues, segmentedCues))
  const segmentActive = segmentOn && canSegment && !editedCues
  const masterCues = editedCues ?? (segmentActive ? segmentedCues : baseCues)

  // The good cut needs the audio, and /history never has it — so it is stored
  // with the entry, once the envelope has had its say
  useEffect(() => {
    if (!result?.job_id || !envelopeReady || !canSegment || !segmentedCues) return
    if (hasEditsRef.current) return
    void updateHistorySegmented(result.job_id, segmentedCues)
  }, [result?.job_id, envelopeReady, canSegment, segmentedCues])

  /** Flipping the switch swaps the cue set everything derives from, so the
   * translations (made from the other set) are dropped — the same rule an
   * edit already follows. */
  function handleSegmentToggle(on: boolean) {
    if (on === segmentOn) return
    setSegmentOn(on)
    writeSegmentPref(on)
    trackSubtitleSegmentation(on, 'result')
    setTranslations({})
    setTargetLang('')
    setTranslateError(null)
  }

  /** Pin a corrected cue set: from here on every view, download and history
   * copy derives from it, and the translations made from the old text go.
   *
   * Which half of the history entry it lands in depends on which set was on
   * screen. A fix made to subtitle lines belongs to the cut — writing it over
   * the stored formats would leave /history with one text under both switch
   * positions and no way back to the transcript. A fix made to the transcript
   * replaces the formats, and the cut with them. */
  function pinCues(updated: Cue[], keepTranslations?: Record<string, string[]>) {
    const editedTheCut = segmentActive
    setEditedCues(updated)
    hasEditsRef.current = true
    // An edit invalidates the translations — the words changed. A delete only
    // removes a line, so the ones that remain are still translated correctly
    // and are carried over with that line taken out of each of them.
    if (keepTranslations) {
      setTranslations(keepTranslations)
    } else {
      setTranslations({})
      setTargetLang('')
    }
    setTranslateError(null)
    if (!result?.job_id) return
    if (editedTheCut) {
      void updateHistorySegmented(result.job_id, updated)
      return
    }
    const formats: Record<string, string> = {}
    for (const { format: fmt } of EXPORT_FORMATS) {
      formats[fmt] = serializeCues(fmt, updated)
    }
    void updateHistoryFormats(result.job_id, formats, true)
    // The stored cut no longer matches the text — the edit is the truth now
    void updateHistorySegmented(result.job_id, null)
  }

  /** A cue's text was corrected. */
  function handleCueEdit(idx: number, text: string) {
    if (!masterCues) return
    trackCueEdited('original')
    pinCues(masterCues.map((c, i) => (i === idx ? { ...c, text } : c)))
  }

  /** A line was dropped — the engine repeats a phrase now and then, and a
   * repeated line is faster to delete than to edit away. Undoable while the
   * toast is up: what is remembered is the cue set as it was, so undoing a
   * delete made on an untouched transcript leaves it untouched. */
  function handleCueDelete(idx: number) {
    if (!masterCues || masterCues.length < 2) return
    trackCueDeleted()
    undoRef.current = {
      cues: editedCues,
      hadEdits: hasEditsRef.current,
      segmented: segmentActive,
      translations,
    }
    // The line goes from the original AND from every translation of it: the
    // two sets are shown as one list and have to stay the same length
    const trimmed: Record<string, string[]> = {}
    for (const [lang, texts] of Object.entries(translations)) {
      trimmed[lang] = texts.length === masterCues.length ? texts.filter((_, i) => i !== idx) : texts
    }
    pinCues(
      masterCues.filter((_, i) => i !== idx),
      trimmed,
    )
    setShowUndo(true)
  }

  function undoDelete() {
    const previous = undoRef.current
    if (!previous) return
    setEditedCues(previous.cues)
    hasEditsRef.current = previous.hadEdits
    setTranslations(previous.translations)
    setTranslateError(null)
    if (result?.job_id) {
      if (previous.cues && !previous.segmented) {
        const formats: Record<string, string> = {}
        for (const { format: fmt } of EXPORT_FORMATS) {
          formats[fmt] = serializeCues(fmt, previous.cues)
        }
        void updateHistoryFormats(result.job_id, formats, true)
      } else if (previous.cues) {
        void updateHistorySegmented(result.job_id, previous.cues)
      } else {
        // Nothing was pinned before the delete: the history goes back to the
        // server formats, and the stored cut comes back with them
        void updateHistoryFormats(result.job_id, serverFormats, formatsComplete)
        if (canSegment && segmentedCues) void updateHistorySegmented(result.job_id, segmentedCues)
      }
    }
    undoRef.current = null
    setShowUndo(false)
  }

  /** Fixing a translated line only touches the translation cache for that
   * language — the original cues (and the history copy) stay untouched. */
  function handleTranslatedCueEdit(idx: number, text: string) {
    if (!targetLang) return
    trackCueEdited('translated')
    setTranslations((prev) => {
      const current = prev[targetLang]
      if (!current) return prev
      return { ...prev, [targetLang]: current.map((t, i) => (i === idx ? text : t)) }
    })
  }

  // Local playback — nothing is fetched from anywhere. A long recording plays
  // the rendition built while it was cut up, because `<audio>` cannot seek
  // the VBR file the user chose: it reports the time you asked for and plays
  // somewhere else, by up to 14 seconds on an hour-long file. Everything
  // short enough not to be cut still plays the original — except a WMA,
  // which no browser can play at all: it waits for the MP3 copy encoded
  // beside its upload, and until that lands the player is simply not offered
  // rather than offered with nothing to play.
  // Created (and revoked) inside the effect so StrictMode's double-run in dev
  // can't leave a revoked URL behind.
  const playFile = playbackAudio?.file ?? (sourceFile && isWma(sourceFile) ? null : sourceFile)
  const playSpans = playbackAudio?.spans ?? EMPTY_SPANS
  // Armed state of the cancel link. It has to be dropped when the run ends,
  // or the next one opens with the link already saying "tap again to stop".
  const [cancelArmed, setCancelArmed] = useState(false)
  useEffect(() => {
    if (!cancelArmed) return
    // A link stuck in "are you sure?" is its own small alarm — it clears
    // itself rather than waiting for a decision nobody is making.
    const id = setTimeout(() => setCancelArmed(false), 4500)
    return () => clearTimeout(id)
  }, [cancelArmed])
  useEffect(() => {
    if (!isLoading) setCancelArmed(false)
  }, [isLoading])

  const [audioUrl, setAudioUrl] = useState<string | null>(null)
  useEffect(() => {
    if (!playFile) {
      setAudioUrl(null)
      return
    }
    const url = URL.createObjectURL(playFile)
    setAudioUrl(url)
    return () => URL.revokeObjectURL(url)
  }, [playFile])

  // Loudness envelope of the uploaded file, for subtitle segmentation. Purely
  // best-effort: null (a long file, an exotic codec, no OfflineAudioContext)
  // only means the re-cut falls back to proportional timings.
  useEffect(() => {
    setEnvelope(null)
    setEnvelopeReady(false)
    if (!sourceFile) {
      setEnvelopeReady(true)
      return
    }
    let cancelled = false
    void loudnessEnvelope(sourceFile).then((env) => {
      if (cancelled) return
      setEnvelope(env)
      setEnvelopeReady(true)
    })
    return () => {
      cancelled = true
    }
  }, [sourceFile])

  async function handleTargetChange(lang: string) {
    setTargetLang(lang)
    setTranslateError(null)
    if (!lang || !result?.result) return
    // Cache hit — unless it was translated before the cues arrived and no
    // longer lines up with them (then re-translate against the cues)
    const cached = translations[lang]
    if (cached && (!masterCues || cached.length === masterCues.length)) return

    setIsTranslating(true)
    try {
      const segments = masterCues
        ? masterCues.map((c) => c.text)
        : [serverFormats[result.output_format] ?? result.result]
      const resp = await apiPost<TranslateApiResponse>('/translate', {
        segments,
        target_lang: lang,
        source_lang: language || null,
      })
      setTranslations((prev) => ({ ...prev, [lang]: resp.segments }))
      trackTranslated({ source: language, target: lang })
    } catch (e) {
      setTranslateError(e instanceof Error ? e.message : t('result.translationFailed'))
      setTargetLang('')
    } finally {
      setIsTranslating(false)
    }
  }

  if (isLoading) {
    // A recording sent in parts gets three lines, each answering a different
    // question: what is happening and how long it has left; how much of the
    // RECORDING is done, in minutes the person recognises; and the part
    // count, small, for whoever wants to know why it takes a while. The old
    // single line read "Uploading… 74%" through all of it — including the
    // tens of seconds per part when nothing was being uploaded at all.
    /* Two taps, not a dialog. A stray tap costs the minutes already waited
       and nothing else — no transcription is charged and the file stays — so
       a modal would price it like cancelling a subscription. Give everything
       the same ceremony and people stop reading any of it.

       It belongs to the WAIT, not to one branch of it. Locked inside the
       chunked block it was missing from the two places that need it most: a
       single request can hold the line for two minutes with no way out, and
       cutting an eight-hour file takes tens of seconds plus a 30 MB
       download. `cancel()` already handles both — `cutIntoChunks` checks the
       signal on every progress callback — there was just nothing to press. */
    const cancelLink = onCancel ? (
      <button
        type="button"
        onClick={() => (cancelArmed ? onCancel() : setCancelArmed(true))}
        className={`mt-1 text-xs underline underline-offset-2 transition-colors ${
          cancelArmed ? 'text-error font-semibold' : 'text-text-muted hover:text-text'
        }`}
      >
        {cancelArmed ? t('result.cancelConfirm') : t('common.cancel')}
      </button>
    ) : null

    if (progress && progress.parts != null && progress.parts > 1 && progress.stage !== 'splitting') {
      const eta =
        progress.etaSeconds == null
          ? null
          : progress.etaSeconds < 60
            ? t('result.etaUnderMinute')
            : t('result.etaLeft', {
                // Words in the sentence, abbreviations in the counter below
                time: formatDuration(roundUpToMinute(progress.etaSeconds) * 60, locale, 'long'),
              })
      const stage = progress.stage === 'sending' ? t('result.sending') : t('result.recognising')
      return (
        // Narrower padding on a phone: the scale's right-hand label names the
        // recording, which is several words in most languages, and every
        // pixel it can have is one it does not have to wrap.
        <div className="p-5 sm:p-8 flex flex-col items-center gap-2.5">
          {/* The estimate is joined on with a dash rather than built into the
              sentence: it comes and goes, and no catalog should have to carry
              two versions of the same line for that. */}
          <p className="text-sm text-text-secondary text-center">{eta ? `${stage} — ${eta}` : stage}</p>
          {/* The bar IS the recording, with its ends named — the same object
              as the player's own scrubber, which is why it reads without
              being explained. An unlabelled rail with the same numbers spelt
              out underneath was three pictures of one fact, and the rail was
              the one you could not read. */}
          <div className="w-full max-w-sm mt-1">
            {/* The two numbers are the bar's scale, so on their own they say
                nothing to a screen reader — `result.doneOfTotal` is the
                sentence they stand for, and it is what gets read out. */}
            <div
              className="h-2 bg-surface-hover rounded-full overflow-hidden"
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={uploadProgress ?? 0}
              aria-label={
                progress.secondsDone != null && progress.secondsTotal != null
                  ? t('result.doneOfTotal', {
                      done: formatDuration(progress.secondsDone, locale),
                      total: formatDuration(progress.secondsTotal, locale),
                    })
                  : undefined
              }
            >
              <div
                className="h-full bg-primary rounded-full transition-all duration-200"
                style={{ width: `${uploadProgress ?? 0}%` }}
              />
            </div>
            {/* The right end carries a noun and the left does not, because only
                the right one is the SCALE. A bare "2 hr" at the end of a
                progress bar reads as the wait — that is what the same number
                means on a player's scrubber, where the recording and the
                sitting-through are the same thing. Here a two-hour file is
                done in ten minutes. */}
            {progress.secondsDone != null && progress.secondsTotal != null && (
              <div className="mt-1.5 flex justify-between gap-3 text-xs text-text-muted tabular-nums">
                {/* The left one is two characters and must never be the thing
                    that wraps — "1 мин" broken across two lines is the whole
                    row looking broken. */}
                <span className="shrink-0">{formatDuration(progress.secondsDone, locale)}</span>
                <span className="text-right">
                  {t('result.ofRecording', {
                    total: formatDuration(progress.secondsTotal, locale),
                  })}
                </span>
              </div>
            )}
          </div>
          <p className="text-[11px] text-text-muted opacity-75">
            {t('result.part', { done: progress.part ?? 1, total: progress.parts })}
          </p>
          {cancelLink}
        </div>
      )
    }

    // One file, one request: unchanged. The bar is the bytes going up, which
    // for a file small enough not to be cut is most of the wait.
    const uploading = progress?.stage === 'sending' && uploadProgress != null && uploadProgress < 100
    const splitting = progress?.stage === 'splitting'
    return (
      <div className="p-8 flex flex-col items-center gap-3">
        {uploading ? (
          <>
            <div className="w-full max-w-xs h-2 bg-surface-hover rounded-full overflow-hidden">
              <div
                className="h-full bg-primary rounded-full transition-all duration-200"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
            <p className="text-sm text-text-secondary">{t('result.uploading', { percent: uploadProgress ?? 0 })}</p>
            {cancelLink}
          </>
        ) : (
          <>
            <LoadingSpinner size="lg" />
            <p className="text-sm text-text-secondary">
              {splitting ? t('result.splitting') : t('result.transcribing')}
            </p>
            {!splitting && <p className="text-xs text-text-muted">{t('result.longFiles')}</p>}
            {cancelLink}
          </>
        )}
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="flex items-start gap-3 p-4 bg-error/5 border border-error/10 rounded-xl">
          <svg className="w-5 h-5 text-error shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
          <div>
            <p className="text-sm font-medium text-error">{t('result.failed')}</p>
            <p className="text-sm text-text-secondary mt-1">{error}</p>
          </div>
        </div>
        <button
          onClick={onReset}
          className="mt-3 text-sm text-primary hover:underline"
        >
          {t('result.tryAgain')}
        </button>
      </div>
    )
  }

  if (!result?.result) return null

  // The charged format's content as it entered the panel (polished when the
  // engine returned caseless text) — the last-resort fallback everywhere
  const chargedContent = serverFormats[result.output_format] ?? result.result

  const translatedTexts = targetLang ? translations[targetLang] : undefined
  // Translated cues are valid only when the translation was made from cues
  const translatedCues: Cue[] | null =
    masterCues && translatedTexts && translatedTexts.length === masterCues.length
      ? masterCues.map((c, i) => ({ ...c, text: translatedTexts[i] }))
      : null

  // Original views come from the server; client-side conversion covers
  // formats that haven't arrived yet (or failed), every translated view, and
  // everything after a user edit (edits supersede the server texts)
  function contentForFormat(fmt: OutputFormat): string | null {
    // With the switch on, these cues are subtitle LINES — one sentence may be
    // spread over three of them, which only plain text has to care about
    const asLines = { fromSubtitleLines: segmentActive }
    if (translatedTexts) {
      return translatedCues ? serializeCues(fmt, translatedCues, asLines) : translatedTexts.join('\n')
    }
    if (editedCues) return serializeCues(fmt, editedCues, asLines)
    // Segmentation on: the server texts still carry the old line breaks, so
    // every format — shown, copied, downloaded, zipped — is serialized from
    // the re-cut cues instead
    if (segmentActive && masterCues) return serializeCues(fmt, masterCues, asLines)
    return (
      serverFormats[fmt] ??
      (masterCues ? serializeCues(fmt, masterCues) : formatsComplete ? chargedContent : null)
    )
  }
  const displayText = contentForFormat(format)

  const displayLang = translatedTexts ? targetLang : language
  const isRtl = displayLang ? RTL_LANGUAGE_CODES.has(displayLang) : false
  const langSuffix = translatedTexts ? targetLang : undefined

  // "Download all" needs to be able to produce all eight files, which is the
  // moment timed cues exist — every format serializes from them, byte for byte
  // what the gateway would have sent. It used to wait for the server sweep to
  // finish instead, from back when the two differed; waiting bought nothing
  // but a delay (and an archive mixing files from different recognition runs).
  // Without cues there is nothing to derive from, so there it still waits.
  const downloadAllReady =
    (!!masterCues || formatsComplete) && (translatedTexts ? translatedCues !== null : true)

  // Interactive view: local audio + timed cues (translated ones when aligned)
  const playerCues = translatedTexts ? translatedCues : masterCues
  const playerAvailable = !!(playerCues && audioUrl)
  // Timestamps arrive with the first background subtitle format a few seconds
  // after a plain-text result — show the tab as "coming" instead of popping in
  const playerPending = !!audioUrl && !playerCues && !formatsComplete
  const effectiveView = view === 'player' && playerAvailable ? 'player' : 'text'

  function buildAllFormatFiles(): { name: string; content: string }[] {
    const suffix = langSuffix ? `.${langSuffix}` : ''
    return EXPORT_FORMATS.map(({ format: fmt, ext }) => ({
      name: `transcription${suffix}.${ext}`,
      content: contentForFormat(fmt) ?? chargedContent,
    }))
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="text-sm font-semibold text-text">{t('result.title')}</h3>
        {/* wrap: on a 320 px phone Copy + Download + "Preparing subtitle
            timings" is wider than the card, and without this the row ran off
            the screen instead of onto a second line */}
        <div className="flex items-center gap-1 flex-wrap">
          {displayText !== null && (
            <>
              <CopyButton text={displayText} onCopied={handleCopied} />
              <DownloadButton text={displayText} format={format} langSuffix={langSuffix} />
            </>
          )}
          {downloadAllReady ? (
            <DownloadAllButton
              getFiles={buildAllFormatFiles}
              zipName={langSuffix ? `transcription.${langSuffix}.zip` : 'transcription.zip'}
            />
          ) : !formatsComplete ? (
            <span
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-text-muted min-w-0"
              title={t('result.preparingFormatsTitle')}
            >
              <LoadingSpinner size="sm" />
              {t('result.preparingFormats')}
            </span>
          ) : null}
        </div>
      </div>

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <span className="text-sm text-text-secondary shrink-0">{t('result.translateTo')}</span>
        {/* Fixed 224 px does not fit beside its label on a narrow phone —
            full width there, its own column from `sm` up */}
        <div className="w-full sm:w-56">
          <Select
            options={targetOptions}
            value={targetLang}
            onChange={handleTargetChange}
            placeholder={t('result.original')}
            searchable
          />
        </div>
        {isTranslating && <LoadingSpinner size="sm" />}
        <span className="text-xs text-text-secondary basis-full sm:basis-auto">
          {t('result.freeNote')}
        </span>
      </div>

      {translateError && (
        <p className="text-sm text-error mb-3">{translateError}</p>
      )}

      {/* Two switches of the same shape — what the lines are cut to, and which
          way the result is shown. They share a row and wrap on narrow screens. */}
      {(canSegment || playerAvailable || playerPending) && (
        <div className="flex items-center gap-x-5 gap-y-2 mb-3 flex-wrap">
          {canSegment && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-text-secondary">{t('segment.label')}</span>
              <div className={PILL_SWITCH_FRAME}>
                {([false, true] as const).map((on) => (
                  <button
                    key={String(on)}
                    onClick={() => handleSegmentToggle(on)}
                    disabled={!!editedCues}
                    title={
                      editedCues
                        ? t('segment.lockedAfterEdit')
                        : on
                          ? t('segment.subtitlesTitle')
                          : t('segment.transcriptTitle')
                    }
                    className={`${PILL_SWITCH_BUTTON} ${
                      segmentOn === on
                        ? 'bg-primary/10 text-primary'
                        : editedCues
                          ? 'text-text-muted cursor-default'
                          : 'text-text-secondary hover:bg-surface-hover cursor-pointer'
                    }`}
                  >
                    {on ? t('segment.subtitles') : t('segment.transcript')}
                  </button>
                ))}
              </div>
            </div>
          )}

          {(playerAvailable || playerPending) && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-text-secondary">{t('result.viewLabel')}</span>
              <div className={PILL_SWITCH_FRAME}>
                <button
                  onClick={() => playerAvailable && setView('player')}
                  disabled={!playerAvailable}
                  title={playerAvailable ? undefined : t('result.preparingTimestamps')}
                  className={`${PILL_SWITCH_BUTTON} inline-flex items-center gap-1.5 ${
                    effectiveView === 'player'
                      ? 'bg-primary/10 text-primary cursor-pointer'
                      : playerAvailable
                        ? 'text-text-secondary hover:bg-surface-hover cursor-pointer'
                        : 'text-text-muted cursor-default'
                  }`}
                >
                  {t('result.player')}
                  {playerPending && <LoadingSpinner size="sm" />}
                </button>
                <button
                  onClick={() => setView('text')}
                  className={`${PILL_SWITCH_BUTTON} cursor-pointer ${
                    effectiveView === 'text'
                      ? 'bg-primary/10 text-primary'
                      : 'text-text-secondary hover:bg-surface-hover'
                  }`}
                >
                  {format === 'text' ? t('result.textTab') : formatLabel(format)}
                </button>
              </div>
            </div>
          )}

          {editedCues && canSegment && (
            <span className="text-xs text-text-muted">{t('segment.lockedAfterEdit')}</span>
          )}
        </div>
      )}

      {effectiveView === 'text' && playerAvailable && (
        <p className="text-xs text-text-muted -mt-1 mb-3">{t('result.editHint')}</p>
      )}

      {/* The player stays mounted (just hidden) while the text view is shown —
          switching tabs must not stop playback or lose the position */}
      {playerAvailable && playerCues && audioUrl && (
        <div className={`bg-surface-alt border border-border rounded-xl p-4 ${effectiveView === 'player' ? '' : 'hidden'}`}>
          {isTranslating && (
            <div className="flex items-center gap-2 text-sm text-text-secondary mb-3">
              <LoadingSpinner size="sm" />
              {t('result.translating')}
            </div>
          )}
          <CuePlayer
            cues={playerCues}
            audioUrl={audioUrl}
            spans={playSpans}
            isRtl={isRtl}
            onCueChange={translatedTexts ? handleTranslatedCueEdit : handleCueEdit}
            onCueDelete={handleCueDelete}
          />
        </div>
      )}
      {effectiveView === 'text' && (
        <div className="bg-surface-alt border border-border rounded-xl p-4 max-h-80 overflow-y-auto" dir={isRtl ? 'rtl' : undefined}>
          {isTranslating ? (
            <div className="flex items-center gap-2 text-sm text-text-secondary">
              <LoadingSpinner size="sm" />
              {t('result.translating')}
            </div>
          ) : displayText === null ? (
            <div className="flex items-center gap-2 text-sm text-text-secondary">
              <LoadingSpinner size="sm" />
              {t('result.preparingFormat', { fmt: formatLabel(format) })}
            </div>
          ) : (
            <p dir={subtitleDocDir(format)} className="text-sm text-text leading-relaxed whitespace-pre-wrap">
              {displayText}
            </p>
          )}
        </div>
      )}

      <Toast message={t('result.copied')} isVisible={showCopied} onHide={handleHideCopied} />
      <Toast
        message={t('cue.deleted')}
        isVisible={showUndo}
        onHide={handleHideUndo}
        duration={6000}
        actionLabel={t('common.undo')}
        onAction={undoDelete}
      />
    </div>
  )
}
